/* eslint-disable react/jsx-one-expression-per-line, max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
    Button,
    Flex,
    Spacer,
    Text,
    TextButton,
    Radio,
    LabeledControl,
    Label,
} from '@zillow/constellation';

import LeadGuaranteeBanner from 'app/shared/components/postingPath/LeadGuaranteeBanner';
import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';
import {
    StyledMDActionsContainer,
    StyledMDContainer,
    StyledMDContent,
    StyledMDContentContainer,
} from 'app/shared/containers/ModalContainer/StyledComponents';

import { StyledIcon } from './StyledComponents';

const { DEACTIVATE_OPTION } = PaidInclusionConstants;

const PaidInclusionDeactivationModal = ({
    onCancelClick,
    onGotItClick,
    onDeactivate,
    expirationDate,
    autoBillEnabled,
    isDeactivationScheduled,
    errorMessage,
    isEligibleForLeadGuarantee,
}) => {
    const [deactivateOn, setDeactivateOn] = useState(autoBillEnabled ? '' : DEACTIVATE_OPTION.NOW);
    const isDeactivateNowSelected = deactivateOn === DEACTIVATE_OPTION.NOW;
    const isDeactivateEotSelected = deactivateOn === DEACTIVATE_OPTION.EOT;

    const renderLeadGuaranteeBanner = () => {
        const heading = 'Backed by our lead guarantee';
        const body = `Your listing is still eligible for our lead guarantee.
            In order to qualify for the lead guarantee again, this listing must remain off-market for 30 consecutive days.`;

        return (
            <LeadGuaranteeBanner
                heading={heading}
                body={body}
                paddingXLOnDesktop={true}
            />
        );
    };

    if (errorMessage) {
        return (
            <Spacer marginBottom="sm">
                <Text>{errorMessage}</Text>
            </Spacer>
        );
    }

    if (isDeactivationScheduled) {
        return (
            <StyledMDContainer className="PaidInclusionDeactivationModal">
                <StyledMDContentContainer>
                    <StyledMDContent>
                        <Flex
                            display="flex"
                            flexDirection="row"
                            justifyContent="center"
                        >
                            <Spacer marginBottom="md">
                                <StyledIcon />
                            </Spacer>
                        </Flex>

                        <Spacer marginBottom="md">
                            <Text type="h2" data-test="deactivation-date">Your listing will deactivate on {expirationDate}.</Text>
                        </Spacer>
                        <Spacer marginBottom="md">
                            <Text type="body">
                            When your listing deactivates, it will no longer be visible to prospective renters. You may
                            activate the listing again at any time.
                            </Text>
                        </Spacer>
                    </StyledMDContent>
                </StyledMDContentContainer>

                <StyledMDActionsContainer className="PIDM-ActionsContainer">
                    <Flex
                        display="flex"
                        flexDirection="row"
                        justifyContent="flex-end"
                    >
                        <Button type="primary" full={true} onClick={onGotItClick}>
                            Got it
                        </Button>
                    </Flex>
                </StyledMDActionsContainer>
            </StyledMDContainer>
        );
    }

    return (
        <StyledMDContainer className="PaidInclusionDeactivationModal">
            <StyledMDContentContainer>
                <StyledMDContent>
                    {isEligibleForLeadGuarantee && (
                        <Spacer>
                            {renderLeadGuaranteeBanner()}
                        </Spacer>
                    )}

                    <Spacer marginY="lg">
                        <Spacer marginBottom="md">
                            <Text type="body" data-test="renew-or-expire">
                        Your listing {autoBillEnabled ? 'is scheduled to renew' : 'will expire'} on&nbsp;
                                <strong>{expirationDate}</strong>. If you deactivate this listing it will not be charged again.
                            </Text>
                        </Spacer>

                        <Spacer
                            marginBottom="md"
                            data-test="deactivate-now-spacer-row"
                        >
                            <LabeledControl
                                label={<Label>Deactivate listing now.</Label>}
                                data-test="deactivate-now-radio"
                                control={(
                                    <Radio
                                        name={DEACTIVATE_OPTION.NOW}
                                        onChange={() => setDeactivateOn(DEACTIVATE_OPTION.NOW)}
                                        checked={isDeactivateNowSelected}
                                    />
                                )}
                            />
                            {isDeactivateNowSelected && (
                                <Text type="body">Your listing will no longer be visible to prospective renters.</Text>
                            )}
                        </Spacer>

                        {autoBillEnabled && (
                            <Spacer
                                marginBottom="md"
                                data-test="deactivate-eot-spacer-row"
                            >
                                <LabeledControl
                                    label={<Label>{`Deactivate listing on ${expirationDate}.`}</Label>}
                                    control={(
                                        <Radio
                                            name={DEACTIVATE_OPTION.EOT}
                                            onChange={() => setDeactivateOn(DEACTIVATE_OPTION.EOT)}
                                            checked={isDeactivateEotSelected}
                                            data-test="deactivate-eot-radio"
                                        />
                                    )}
                                />
                                {isDeactivateEotSelected && (
                                    <Text fontType="body">
                                Your listing will remain active until&nbsp;
                                        <strong>{expirationDate}</strong>. When it expires, it will no longer be visible to
                                prospective renters.
                                    </Text>
                                )}
                            </Spacer>
                        )}
                    </Spacer>
                </StyledMDContent>
            </StyledMDContentContainer>


            <StyledMDActionsContainer className="PIDM-ActionsContainer">
                <Flex
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    <Spacer marginRight="md">
                        <TextButton
                            data-test="cancel-button"
                            buttonType="secondary"
                            onClick={onCancelClick}
                        >
                            Cancel
                        </TextButton>
                    </Spacer>

                    <Spacer>
                        <Button
                            data-test="deactivate-listing-button"
                            buttonType="primary"
                            disabled={!deactivateOn}
                            onClick={() => onDeactivate(deactivateOn)}
                        >
                            Confirm
                        </Button>
                    </Spacer>
                </Flex>
            </StyledMDActionsContainer>
        </StyledMDContainer>
    );
};

PaidInclusionDeactivationModal.defaultProps = {
    isDeactivationScheduled: false,
    errorMessage: '',
    isEligibleForLeadGuarantee: false,
};

PaidInclusionDeactivationModal.propTypes = {
    onCancelClick: PropTypes.func.isRequired,
    onGotItClick: PropTypes.func.isRequired,
    onDeactivate: PropTypes.func.isRequired,
    expirationDate: PropTypes.string.isRequired,
    autoBillEnabled: PropTypes.bool.isRequired,
    isDeactivationScheduled: PropTypes.bool,
    errorMessage: PropTypes.string,
    isEligibleForLeadGuarantee: PropTypes.bool,
};

export default PaidInclusionDeactivationModal;
